import React, { useEffect, useRef, useState } from "react";
import { Gem, BadgeCheck, ExternalLink } from "lucide-react";
import { useSolitaireSettings } from "@/context/SolitaireSettingsContext";
import { computeSolitairePrice, fmtINR } from "@/lib/solitairePricing";
import { buildCertVerifyUrl } from "@/lib/solitaireCert";
import Solitaire360Frame from "@/components/Solitaire360Frame";

/**
 * Listing card.
 * - 360° viewer auto-loads via IntersectionObserver, held inside a strict
 *   1:1 aspect-ratio box so cards never break the grid even on iframe
 *   load delays.
 * - When the lab + cert no are available we show a "Verify" badge that
 *   opens IGI/GIA/HRD verification in a new tab — no server-rendered cert
 *   cards anymore.
 */
export default function SolitaireCard({ item, onOpen, index = 0 }) {
  const { usdRate, markupKey, gstOn } = useSolitaireSettings();
  const price = computeSolitairePrice(item, usdRate, markupKey, gstOn);
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!rootRef.current || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setInView(true); io.disconnect(); break; }
        }
      },
      { rootMargin: "300px 0px", threshold: 0.01 }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [inView]);

  const verifyUrl = buildCertVerifyUrl(item.cert_lab, item.cert_no);

  return (
    <div
      ref={rootRef}
      className="group relative bg-white border border-gold/15 hover:border-gold hover:shadow-xl cursor-pointer transition-all duration-300 fade-up"
      style={{ animationDelay: `${Math.min(index, 16) * 40}ms` }}
      onClick={() => onOpen?.(item)}
      data-testid={`solitaire-card-${item.stock_id}`}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "1 / 1", background: "linear-gradient(135deg,#F8F6EF,#EFE6CC)" }}
      >
        {inView && item.video_link ? (
          <Solitaire360Frame
            src={item.video_link}
            title={`360° ${item.stock_id}`}
            interactive={false}
            testId={`solitaire-360-card-${item.stock_id}`}
            className="absolute inset-0"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Gem className="w-20 h-20 transition-transform duration-500 group-hover:scale-110" style={{ color: "#C9A84C" }} strokeWidth={0.6} />
          </div>
        )}
        {/* Click-overlay so the whole tile remains clickable even with iframe inside */}
        <div className="absolute inset-0" aria-hidden="true" />
        <div className="absolute top-3 left-3 px-2 py-1 text-[10px] tracking-[0.25em] uppercase font-medium z-10" style={{ background: "rgba(11,26,51,0.85)", color: "#D9BE6E", border: "1px solid rgba(217,190,110,0.4)" }}>
          {item.shape || "DIAMOND"}
        </div>
        {item.cert_lab && (
          verifyUrl ? (
            <a
              href={verifyUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute top-3 right-3 px-2 py-1 text-[10px] tracking-[0.25em] uppercase font-medium flex items-center gap-1 z-10 hover:bg-gold/10"
              style={{ background: "rgba(255,255,255,0.95)", color: "#0B1A33", border: "1px solid rgba(11,26,51,0.15)" }}
              data-testid={`solitaire-card-verify-${item.stock_id}`}
              aria-label={`Verify ${item.cert_lab} certificate`}
            >
              <BadgeCheck className="w-3 h-3" style={{ color: "#A88934" }} /> {item.cert_lab}
              <ExternalLink className="w-3 h-3" style={{ color: "rgba(11,26,51,0.55)" }} />
            </a>
          ) : (
            <div className="absolute top-3 right-3 px-2 py-1 text-[10px] tracking-[0.25em] uppercase font-medium flex items-center gap-1 z-10" style={{ background: "rgba(255,255,255,0.95)", color: "#0B1A33", border: "1px solid rgba(11,26,51,0.15)" }}>
              <BadgeCheck className="w-3 h-3" style={{ color: "#A88934" }} /> {item.cert_lab}
            </div>
          )
        )}
      </div>
      <div className="p-5 space-y-2">
        <div className="flex items-center justify-between text-[11px] tracking-widest uppercase" style={{ color: "rgba(11,26,51,0.6)" }}>
          <span>{item.stock_id}</span>
          {item.cert_no && <span style={{ color: "#A88934" }}>Cert {item.cert_no}</span>}
        </div>
        <h3 className="font-display text-lg leading-snug" style={{ color: "#1a0f00", fontWeight: 500 }}>
          {Number(item.carat || 0).toFixed(2)} ct {item.shape || ""}
        </h3>
        <div className="text-xs tracking-wide" style={{ color: "rgba(11,26,51,0.7)" }}>
          {[item.colour, item.clarity, item.cut].filter(Boolean).join(" · ")}
        </div>
        <div className="flex items-baseline justify-between pt-2 border-t" style={{ borderColor: "rgba(201,168,76,0.25)" }}>
          <span className="text-[11px] tracking-widest uppercase" style={{ color: "rgba(11,26,51,0.55)" }}>From</span>
          <span className="font-display text-xl" style={{ color: "#0B1A33", fontWeight: 600 }}>{fmtINR(price.total)}</span>
        </div>
      </div>
    </div>
  );
}
